import { ArrowRight, Check } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, Eyebrow, Section } from "./primitives";
import { AppPreview } from "./AppPreview";

/** The USP: every payment paired with its document, shown in a working mock of the app. */
export function MatchingSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const criteria = [tr("Částka", "Amount"), tr("Datum", "Date"), tr("Dodavatel", "Supplier")];

  return (
    <Section id="parovani" labelledBy="matching-title" contours="frame">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-end lg:gap-16">
          <div>
            <Eyebrow className="mb-3">{tr("Párování", "Matching")}</Eyebrow>
            <h2
              id="matching-title"
              className="text-[1.75rem] font-semibold leading-[1.15] tracking-tight sm:text-4xl sm:leading-[1.1]"
            >
              {tr(
                "Automatické párování plateb s účtenkami a fakturami",
                "Automatic matching of payments with receipts and invoices"
              )}
            </h2>
          </div>
          <div>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              {tr(
                "Každé párování zkontrolujete a jedním klikem zrušíte.",
                "You check every match and undo it with one click."
              )}
            </p>
            {/* What a match is decided by, read left to right. */}
            <ul className="mt-5 flex flex-wrap items-center gap-2 text-[13px]">
              {criteria.map((label) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5"
                >
                  <Check
                    aria-hidden="true"
                    className="size-3.5 text-[hsl(var(--action))] dark:text-primary"
                    strokeWidth={2.5}
                  />
                  {label}
                </li>
              ))}
              <li aria-hidden="true" className="text-muted-foreground">
                <ArrowRight className="size-4" />
              </li>
              <li className="inline-flex items-center rounded-full bg-primary/15 px-3 py-1.5">
                <DotChip color="green" className="text-[13px] text-foreground">
                  {tr("Spárováno", "Matched")}
                </DotChip>
              </li>
            </ul>
          </div>
        </div>
        <AppPreview locale={locale} />
      </Container>
    </Section>
  );
}
